const RATIO_EPSILON = 0.0001;
const MARKUP_EPSILON = 0.001;

function numericRatio(value) {
    if (value === null || value === undefined || value === '') return null;
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    const normalized = String(value).trim().replace(/\s/g, '').replace(',', '.');
    if (!normalized) return null;
    const numeric = Number(normalized);
    return Number.isFinite(numeric) ? numeric : null;
}

function describedRow(row, sale) {
    return {
        code: String(row?.product_code || '').trim(),
        name: String(row?.product_name || '').trim(),
        furnizor: String(row?.furnizor || '').trim(),
        sale,
    };
}

export function saleDelta(row, priorOrNull) {
    const read = typeof priorOrNull === 'function'
        ? priorOrNull
        : (_row, value) => value;
    return numericRatio(read(row, row?.price_adjustment_ratio));
}

export function entryDelta(row, priorOrNull) {
    const read = typeof priorOrNull === 'function'
        ? priorOrNull
        : (_row, value) => value;
    return numericRatio(read(row, row?.entry_price_adjustment_ratio));
}

export function isMarkupBelowReference(row) {
    const actual = numericRatio(row?.markup_actual);
    const reference = numericRatio(row?.markup_reference);
    if (actual == null || reference == null) return false;
    return actual < reference - MARKUP_EPSILON;
}

/**
 * Counts and extremes for the attention band and the pre-apply summary.
 * Ratios use the same prior-price rule as the table (new rows have no delta).
 *
 * @param {Object[]} rows
 * @param {(row: Object, value: unknown) => unknown} [priorOrNull]
 */
export function summarizeDraftRows(rows, priorOrNull) {
    const list = Array.isArray(rows) ? rows : [];
    let saleUp = 0;
    let saleDown = 0;
    let markupBelow = 0;
    let entryUp = 0;
    /** @type {{ code: string, name: string, furnizor: string, sale: number }[]} */
    const ranked = [];
    list.forEach((row) => {
        const sale = saleDelta(row, priorOrNull);
        if (sale != null && sale > RATIO_EPSILON) saleUp += 1;
        if (sale != null && sale < -RATIO_EPSILON) saleDown += 1;
        if (sale != null && Math.abs(sale) > RATIO_EPSILON) {
            ranked.push(describedRow(row, sale));
        }
        const entry = entryDelta(row, priorOrNull);
        if (entry != null && entry > RATIO_EPSILON) entryUp += 1;
        if (isMarkupBelowReference(row)) markupBelow += 1;
    });
    const increases = [...ranked]
        .filter((row) => row.sale > RATIO_EPSILON)
        .sort((a, b) => b.sale - a.sale)
        .slice(0, 3);
    const decreases = [...ranked]
        .filter((row) => row.sale < -RATIO_EPSILON)
        .sort((a, b) => a.sale - b.sale)
        .slice(0, 3);
    return {
        rowCount: list.length,
        saleUp,
        saleDown,
        markupBelow,
        entryUp,
        largestIncrease: increases[0] || null,
        largestDecrease: decreases[0] || null,
        topIncreases: increases,
        topDecreases: decreases,
    };
}

/**
 * @param {Object} row
 * @param {string|{ type: 'code', code: string }|null} filter
 * @param {(row: Object, value: unknown) => unknown} [priorOrNull]
 * @returns {boolean}
 */
export function rowMatchesAttention(row, filter, priorOrNull) {
    if (!filter) return true;
    if (typeof filter === 'object' && filter.type === 'code') {
        return String(row?.product_code || '').trim() === String(filter.code || '').trim();
    }
    if (filter === 'sale-up') {
        const sale = saleDelta(row, priorOrNull);
        return sale != null && sale > RATIO_EPSILON;
    }
    if (filter === 'sale-down') {
        const sale = saleDelta(row, priorOrNull);
        return sale != null && sale < -RATIO_EPSILON;
    }
    if (filter === 'markup-below') return isMarkupBelowReference(row);
    if (filter === 'entry-up') {
        const entry = entryDelta(row, priorOrNull);
        return entry != null && entry > RATIO_EPSILON;
    }
    return true;
}
